import React, { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

const CyberpunkParticles = ({ count = 1500 }) => {
  const meshRef = useRef()
  const linesRef = useRef()
  
  const particles = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    
    // Create cyberpunk-themed particles
    for (let i = 0; i < count; i++) {
      // Position particles in a cyberpunk grid pattern with some randomness
      const x = (Math.random() - 0.5) * 50
      const y = (Math.random() - 0.5) * 30
      const z = (Math.random() - 0.5) * 40
      
      positions[i * 3] = x
      positions[i * 3 + 1] = y
      positions[i * 3 + 2] = z
      
      // Cyberpunk color palette
      const colorChoice = Math.random()
      if (colorChoice < 0.4) {
        // Bright magenta/purple
        colors[i * 3] = 1.0     // R
        colors[i * 3 + 1] = 0.0 // G
        colors[i * 3 + 2] = 1.0 // B
      } else if (colorChoice < 0.7) {
        // Cyan
        colors[i * 3] = 0.0     // R
        colors[i * 3 + 1] = 1.0 // G
        colors[i * 3 + 2] = 1.0 // B
      } else {
        // Hot pink
        colors[i * 3] = 1.0     // R
        colors[i * 3 + 1] = 0.0 // G
        colors[i * 3 + 2] = 0.5 // B
      }
      
      sizes[i] = Math.random() * 2 + 0.5
    }
    
    return { positions, colors, sizes }
  }, [count])

  // Create connecting lines for cyberpunk grid effect
  const lines = useMemo(() => {
    const linePositions = []
    const lineColors = []
    
    // Create grid lines
    for (let i = 0; i < 20; i++) {
      for (let j = 0; j < 20; j++) {
        const x = (i - 10) * 2
        const z = (j - 10) * 2
        
        // Horizontal lines
        linePositions.push(x, 0, z - 20, x, 0, z + 20)
        lineColors.push(1, 0, 1, 0.3, 1, 0, 1, 0.3) // Magenta with alpha
        
        // Vertical lines
        linePositions.push(x - 20, 0, z, x + 20, 0, z)
        lineColors.push(0, 1, 1, 0.3, 0, 1, 1, 0.3) // Cyan with alpha
      }
    }
    
    return {
      positions: new Float32Array(linePositions),
      colors: new Float32Array(lineColors)
    }
  }, [])

  useFrame((state) => {
    if (meshRef.current) {
      // Slow rotation for cyberpunk atmosphere
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.02
      meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.01) * 0.1
      
      // Animate particle sizes for pulsing effect
      const positions = meshRef.current.geometry.attributes.position.array
      const time = state.clock.elapsedTime
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3
        // Add subtle floating animation
        positions[i3 + 1] += Math.sin(time + i * 0.1) * 0.001
      }
      
      meshRef.current.geometry.attributes.position.needsUpdate = true
    }
    
    if (linesRef.current) {
      linesRef.current.rotation.y = state.clock.elapsedTime * 0.005
      // Pulse the opacity
      linesRef.current.material.opacity = 0.1 + Math.sin(state.clock.elapsedTime * 2) * 0.05
    }
  })

  return (
    <>
      {/* Main particle system */}
      <points ref={meshRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={count}
            array={particles.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={count}
            array={particles.colors}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-size"
            count={count}
            array={particles.sizes}
            itemSize={1}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.1}
          vertexColors
          transparent
          opacity={0.8}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
        />
      </points>
      
      {/* Cyberpunk grid lines */}
      <lineSegments ref={linesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={lines.positions.length / 3}
            array={lines.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={lines.colors.length / 4}
            array={lines.colors}
            itemSize={4}
          />
        </bufferGeometry>
        <lineBasicMaterial
          vertexColors
          transparent
          opacity={0.15}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>
    </>
  )
}

export default CyberpunkParticles
